/**
 * TaskCardList — 태스크 카드 목록
 *
 * LinearRenderer 패턴 참고.
 * 각 카드에 상태 뱃지, 담당자, 마감일, 마지막 활동을 표시.
 */
import React from 'react';
import { Trans, t } from '@lingui/macro';
import { useLingui } from '@lingui/react';
import { formatRelativeTime } from '../../lib/relativeTime';
import {
  ListTodo, Clock, CalendarDays, AlertTriangle, MessageCircle, PenSquare,
} from 'lucide-react';
import {
  Post, getTaskMetadata, TASK_STATUS_CONFIG, getTaskStatusLabel,
  getDaysUntilDue,
} from '../../models/Board';
import { cn } from '../../lib/utils';
import { UserAvatar } from '../components/UserAvatar';
import { NotifDot } from '../board/renderers/shared';

interface TaskCardListProps {
  posts: Post[];
  onPostClick?: (post: Post) => void;
  onRefresh?: () => void;
  /** 통지센터 알림(미읽음)이 있는 카드 ID 목록 — 제목 옆 오렌지 점 */
  notifiedPostIds?: Set<string>;
}

/** 마지막 활동으로부터 경과 일수 */
function getDaysSinceActivity(lastActivityAt: string): number {
  const now = new Date();
  const last = new Date(lastActivityAt);
  return Math.floor((now.getTime() - last.getTime()) / (1000 * 60 * 60 * 24));
}

const TaskCardList: React.FC<TaskCardListProps> = ({
  posts,
  onPostClick,
  notifiedPostIds,
}) => {
  const { i18n } = useLingui();
  // 빈 상태
  if (posts.length === 0) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center gap-3 px-6 py-12 text-center">
        <div className="size-12 rounded-xl bg-muted flex items-center justify-center">
          <ListTodo className="size-5 text-muted-foreground" />
        </div>
        <div>
          <p className="text-sm font-medium text-foreground mb-0.5">
            <Trans>No task cards yet</Trans>
          </p>
          <p className="text-meta text-muted-foreground max-w-[240px]">
            <Trans>Create cards from chat threads to start tracking tasks.</Trans>
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 overflow-y-auto">
      <div>
        {posts.map(post => {
          const meta = getTaskMetadata(post);
          if (!meta) return null;

          const statusConf = TASK_STATUS_CONFIG[meta.status];
          const daysSinceActivity = getDaysSinceActivity(meta.last_activity_at);
          const isInactive = meta.status !== 'done' && daysSinceActivity >= 7;

          // 마감일 처리
          let dueBadge: React.ReactNode = null;
          if (meta.due_date) {
            const daysLeft = getDaysUntilDue(meta.due_date);
            const isOverdue = daysLeft < 0 && meta.status !== 'done';
            const isDueSoon = daysLeft >= 0 && daysLeft <= 2 && meta.status !== 'done';
            dueBadge = (
              <span className={cn(
                'inline-flex items-center gap-1.5 text-meta tabular-nums',
                isOverdue ? 'text-destructive font-medium' :
                isDueSoon ? 'text-badge-warn font-medium' :
                'text-muted-foreground'
              )}>
                <CalendarDays className="size-3.5" />
                {isOverdue
                  ? `D+${Math.abs(daysLeft)}`
                  : daysLeft === 0
                    ? t`Today`
                    : `D-${daysLeft}`
                }
              </span>
            );
          }

          return (
            <button
              key={post.id}
              onClick={() => onPostClick?.(post)}
              className="relative w-full flex items-start gap-2.5 px-3.5 py-3 text-left hover:bg-muted transition-colors after:content-[''] after:absolute after:bottom-0 after:left-3.5 after:right-3.5 after:h-px after:bg-border sm:gap-3.5 sm:px-[18px] sm:py-3.5 sm:after:left-[18px] sm:after:right-[18px]"
            >
              {/* 상태 인디케이터 — prototype `.ih-status-dot { background: var(--badge-XX) }` */}
              <div className={cn(
                'mt-[7px] size-[9px] rounded-full shrink-0',
                meta.status === 'requested' && 'bg-badge-info',
                meta.status === 'in_progress' && 'bg-badge-caution',
                meta.status === 'in_review' && 'bg-badge-review',
                meta.status === 'done' && 'bg-badge-success',
              )} />

              {/* 메인 콘텐츠 */}
              <div className="flex-1 min-w-0 flex flex-col gap-1.5">
                {/* 제목 행 — prototype `.ih-card-title { 15px / 600 / 1.4 }`
                    min-w-0: flex 자식은 min-width:auto(=콘텐츠 폭)라 min-w-0 없으면 긴 제목이
                    줄지 못해 truncate 가 안 먹고 뱃지를 화면 밖으로 밀어냄(모바일 가로 잘림). */}
                <div className="flex items-center gap-2.5 min-w-0">
                  <span className={cn(
                    'text-body font-semibold leading-[1.4] truncate min-w-0 sm:text-body-lg sm:font-medium',
                    meta.status === 'done' ? 'text-muted-foreground line-through' : 'text-foreground'
                  )}>
                    {post.title}
                  </span>
                  {notifiedPostIds?.has(post.id) && <NotifDot />}
                  {/* status badge — prototype `.ih-status-badge { 11px / 500 / radius 999px / padding 3 9 }` */}
                  <span className={cn(
                    'shrink-0 text-meta-sm px-[9px] py-[3px] rounded-full font-medium',
                    statusConf.bgColor, statusConf.color
                  )}>
                    {getTaskStatusLabel(meta.status)}
                  </span>
                </div>

                {/* 메타 행 */}
                <div className="flex items-center gap-2 flex-wrap gap-y-1 text-meta-sm text-muted-foreground sm:gap-3 sm:gap-y-0 sm:text-meta">
                  {/* 담당자 — 아바타 + 이름 */}
                  {meta.assignee && (
                    <span className="inline-flex items-center gap-1.5">
                      <UserAvatar
                        src={meta.assignee.photoUrl ?? null}
                        name={meta.assignee.name}
                        className="size-5"
                      />
                      <span className="text-foreground max-w-14 truncate sm:max-w-[100px]">{meta.assignee.name}</span>
                    </span>
                  )}

                  {/* 작성자 — PenSquare 아이콘 + 이름. 담당자와 동일인이면 생략 (중복 정보) */}
                  {post.author && post.author.uid !== meta.assignee?.uid && (
                    <span className="inline-flex items-center gap-1" title={t`Author: ${post.author.name}`}>
                      <PenSquare className="size-3.5 opacity-70" />
                      <span className="max-w-14 truncate sm:max-w-[100px]">{post.author.name}</span>
                    </span>
                  )}

                  {/* 마감일 */}
                  {dueBadge}

                  {/* 마지막 활동 */}
                  <span className="inline-flex items-center gap-1.5">
                    <Clock className="size-3.5 opacity-70" />
                    {formatRelativeTime(meta.last_activity_at, i18n)}
                  </span>

                  {/* 비활동 경고 */}
                  {isInactive && (
                    <span className="inline-flex items-center gap-1.5 text-badge-warn font-medium">
                      <AlertTriangle className="size-3.5" />
                      <Trans>No change for {daysSinceActivity} days</Trans>
                    </span>
                  )}

                  {/* 댓글 수 */}
                  {post.comment_count > 0 && (
                    <span className="inline-flex items-center gap-1.5 tabular-nums">
                      <MessageCircle className="size-3.5 opacity-70" />
                      {post.comment_count}
                    </span>
                  )}
                </div>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
};

/** 로딩 스켈레톤 — 카드 목록 자리 표시 (TaskCardList 행 레이아웃 모방) */
export const TaskCardListSkeleton: React.FC<{ count?: number }> = ({ count = 6 }) => (
  <div className="flex-1 overflow-hidden">
    {Array.from({ length: count }).map((_, i) => (
      <div key={i} className="flex items-start gap-2.5 px-3.5 py-3 animate-pulse sm:gap-3.5 sm:px-[18px] sm:py-3.5">
        <div className="mt-[7px] size-[9px] rounded-full bg-muted shrink-0" />
        <div className="flex-1 min-w-0 flex flex-col gap-1.5">
          <div className="flex items-center gap-2.5">
            <div className="h-4 w-1/2 bg-muted rounded" />
            <div className="h-4 w-14 bg-muted rounded-full shrink-0" />
          </div>
          <div className="flex items-center gap-2">
            <div className="size-5 rounded-full bg-muted" />
            <div className="h-3 w-16 bg-muted rounded" />
          </div>
        </div>
      </div>
    ))}
  </div>
);

export default TaskCardList;
